<template>
    <main class="relative flex flex-col flex-1 min-h-0 bg-stage-background overflow-y-auto">
        <form @submit.prevent="saveProject">
            <div class="px-8 sm:px-8 lg:px-8 pt-6 pb-2">
                <div class="w-full xl:w-1/2 mx-auto flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <h1 class="stage-page-title">Projekt anlegen</h1>
                    <div class="inline-flex">
                        <router-link class="btn-stage-secondary mr-3" to="/workspace/projects">
                            Abbrechen
                        </router-link>
                        <button class="btn-stage-primary" type="submit">
                            Projekt anlegen
                        </button>
                    </div>
                </div>
            </div>
            <div class="stage-page-body">
                <div class="w-full xl:w-1/2 mx-auto bg-white shadow sm:rounded">
                    <loading :status="loading.form"/>
                    <div class="p-6">
                        <div class="grid grid-cols-3 gap-6">
                            <div class="col-span-3">
                                <label class="stage-filter-label block mb-2" for="project-title">Titel</label>
                                <div class="relative">
                                    <input
                                        id="project-title"
                                        v-model="project.title"
                                        class="stage-filter-input form-input block w-full text-sm transition duration-150 ease-in-out sm:leading-5"
                                        placeholder="Titel"
                                        required
                                    >
                                </div>
                            </div>
                            <div class="col-span-3">
                                <label class="stage-filter-label block mb-2" for="project-description">Projektbeschreibung</label>
                                <div class="relative">
                                    <textarea
                                        id="project-description"
                                        v-model="project.description"
                                        class="stage-filter-input form-input block w-full text-sm transition duration-150 ease-in-out sm:leading-5"
                                        placeholder="Projektbeschreibung"
                                        rows="8"
                                    ></textarea>
                                </div>
                            </div>
                            <div class="col-span-3">
                                <label class="block text-sm leading-5 font-medium text-gray-700">Projekt-Bild</label>
                                <div class="mt-2 flex items-center select-none">
                                    <div class="avatar-editor relative inline-block h-12 w-12 overflow-hidden bg-gray-100">
                                        <button
                                            v-show="hasUploadedImage"
                                            class="avatar-editor-remove absolute top-0 right-0 items-center p-0.5 rounded-full leading-4 bg-red-600 text-white cursor-pointer"
                                            type="button"
                                            @click="removeImage"
                                        >
                                            <svg-vue class="h-4 w-4 p-px" icon="font-awesome.times-light"></svg-vue>
                                        </button>
                                        <img
                                            :src="imagePreviewUrl"
                                            alt="Projektbild"
                                            class="h-full w-full text-gray-300 rounded-full object-cover"
                                        />
                                    </div>
                                    <span class="ml-5">
                                        <input ref="changeImage" accept=".png,.jpg,.jpeg" hidden type="file" @change="changeImage($event)">
                                        <button
                                            class="py-2 px-3 border border-bordergray-100 rounded text-sm leading-4 font-medium text-gray-700 bg-white hover:text-gray-600 hover:bg-gray-50 focus:outline-none focus:ring-0 active:bg-gray-50 active:text-gray-800 transition duration-150 ease-in-out"
                                            type="button"
                                            @click="selectImage"
                                        >
                                            Projektbild ändern
                                        </button>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </form>
    </main>
</template>

<script>
export default {
    name: 'dashboard-projects-new',
    metaInfo() {
        return {
            title: 'Projekt anlegen',
        };
    },
    data() {
        return {
            loading: {
                form: false,
            },
            project: {
                title: '',
                description: '',
                image: null,
                image_preview: null,
            },
        };
    },
    computed: {
        hasUploadedImage() {
            if (typeof File === 'undefined') {
                return !!this.project.image;
            }

            return this.project.image instanceof File;
        },
        imagePreviewUrl() {
            if (this.project.image_preview) {
                return this.project.image_preview;
            }

            return this.generateFallbackImage();
        },
    },
    methods: {
        selectImage() {
            this.$refs.changeImage.click();
        },
        removeImage() {
            this.project.image = null;
            this.project.image_preview = null;
            if (this.$refs.changeImage) {
                this.$refs.changeImage.value = '';
            }
        },
        changeImage(event) {
            const self = this;
            if (!event.target.files.length) {
                return;
            }

            const file = event.target.files[0];
            if (['image/png', 'image/x-citrix-png', 'image/x-png', 'image/jpeg', 'image/x-citrix-jpeg'].includes(file.type)) {
                self.project.image = file;
                self.project.image_preview = URL.createObjectURL(file);
                return;
            }

            self.$notify({
                title: self.$i18n.t('Unsupported file type').toString(),
                text: self.$i18n.t('Only image type files are accepted').toString(),
                type: 'error',
            });
            event.target.value = '';
        },
        generateFallbackImage() {
            const seedText = String(this.project.title || 'P').trim() || 'P';
            const firstChar = seedText.charAt(0).toUpperCase();
            const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 80 80" width="80" height="80"><defs><linearGradient id="projectGradientNewDashboard" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="hsl(210 35% 48%)"/><stop offset="100%" stop-color="hsl(210 35% 34%)"/></linearGradient></defs><rect width="80" height="80" fill="url(#projectGradientNewDashboard)"/><text x="50%" y="54%" text-anchor="middle" dominant-baseline="middle" fill="rgba(255,255,255,0.55)" font-family="Arial, sans-serif" font-size="34" font-weight="700">${firstChar}</text></svg>`;
            return 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svg)));
        },
        saveProject() {
            const self = this;
            self.loading.form = true;

            const formData = new FormData();
            formData.append('title', self.project.title || '');
            formData.append('description', self.project.description || '');
            if (self.project.image instanceof File) {
                formData.append('image', self.project.image);
            }

            axios.post('api/workspace/projects', formData, {
                headers: {'Content-Type': 'multipart/form-data'},
            }).then(function () {
                self.loading.form = false;
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data saved correctly').toString(),
                    type: 'success',
                });
                self.$router.push('/workspace/projects');
            }).catch(function () {
                self.loading.form = false;
            });
        },
    },
};
</script>

<style lang="scss">
@reference "../../../../../sass/tailwind-reference.css";

.avatar-editor {
    .avatar-editor-remove {
        @apply hidden;
    }

    &:hover {
        .avatar-editor-remove {
            @apply inline-flex;
        }
    }
}
</style>
